import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { createOrganisation } from '../../redux/actions/organisationActions';
import { organisationCreated } from '../../utils/analytics';
import uploadIcon from '../../assets/images/plus.png';
import UserIcon from '../../assets/images/image upload.svg';
import Button from '../../shared/components/Button';
import ClubbieModal from '../../components/Modal/ClubbieModal';
import Input from '../../shared/components/Input';
import StepsTracker from '../../components/StepsTracker/StepsTracker';
import { DASHBOARD_STACK } from '../../utils/screenNames';

/**
 * OrganisationDetails Page - Step 3 of signup
 * Refactored with Tailwind CSS to match PersonalDetails layout
 */
function OrganisationDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user?.user);

  const [organisationName, setOrganisationName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // const avatarOptions = [
  //   { name: 'org 1.png', src: Avatar1 },
  //   { name: 'org 2.png', src: Avatar2 },
  //   { name: 'org 3.png', src: Avatar3 },
  //   { name: 'org 4.png', src: Avatar4 },
  //   { name: 'org 5.png', src: Avatar5 },
  //   { name: 'org 6.png', src: Avatar6 },
  //   { name: 'org 7.png', src: Avatar7 },
  //   { name: 'org 8.png', src: Avatar8 },
  //   { name: 'org 9.png', src: Avatar9 },
  // ];

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleSaveAvatar = () => {
    if (selectedAvatar) {
      setProfileImage(selectedAvatar);
    } else if (selectedFile && preview) {
      setProfileImage(preview);
    }
    closeModal();
  };

  const handleCancelAvatar = () => {
    setSelectedAvatar('');
    setSelectedFile(null);
    setPreview(null);
    setValidationError(null);
    closeModal();
  };

  const handleAvatarSelect = (avatarName) => {
    setSelectedAvatar(avatarName);
    setSelectedFile(null);
    setPreview(null);
    setValidationError(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setValidationError(null);

    if (!file) {
      setValidationError('Please select a file to upload.');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setValidationError('Invalid file type. Please upload a SVG, PNG, JPG, or GIF image.');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setValidationError('File size too large. Maximum size is 5MB.');
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      setSelectedFile(file);
      setPreview(img.src);
      setSelectedAvatar('');
    };
    img.onerror = () => {
      setValidationError('Invalid image file. Please try again.');
    };
  };

  const handleFinish = async () => {
    setSubmitError('');

    if (!organisationName.trim()) {
      setSubmitError('Please enter your organisation name.');
      return;
    }

    setIsSubmitting(true);

    try {
      const organisationData = { name: organisationName };

      await createOrganisation(
        organisationData,
        (data) => {
          organisationCreated({
            organisationId: data?.id ?? data?._id,
            organisationName: data?.name ?? organisationName,
            user,
          });
          dispatch({ type: 'CREATE_ORG_SUCCESS', payload: data });
          setIsSubmitting(false);
          navigate(DASHBOARD_STACK.HOME);
        },
        (error) => {
          dispatch({ type: 'CREATE_ORG_FAIL', payload: error.message });
          setIsSubmitting(false);
          setSubmitError('Failed to create organisation. Please try again.');
        },
      );
    } catch (err) {
      setIsSubmitting(false);
      setSubmitError('An unexpected error occurred. Please try again.');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderAvatarImage = () => {
    if (!profileImage) {
      return <img src={UserIcon} alt="Organisation" className="w-10 h-10 opacity-50" />;
    }

    // const avatar = avatarOptions.find((a) => a.name === profileImage);
    // if (avatar) {
    //   return <img src={avatar.src} alt="Organisation" className="w-full h-full rounded-full object-cover" />;
    // }

    return <img src={profileImage} alt="Organisation" className="w-full h-full rounded-full object-cover" />;
  };

  return (
    <div className="relative w-screen min-h-screen bg-bg-dark overflow-y-auto">
      {/* Logo - Top Right */}
      <div className="absolute top-4 right-6 z-10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
            <span className="text-bg-dark font-bold text-xl">C</span>
          </div>
          <span className="text-[#FEFCEB] font-semibold text-xl">CLUBBIE</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-xl px-6 py-8 mx-auto">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-light text-[#FEFCEB] mb-1">Create your account</h1>
          <p className="text-shaded-text text-sm">Enter your details to create your account.</p>
        </div>

        {/* Steps Tracker */}
        <div className="mb-6">
          <StepsTracker currentStep={3} />
        </div>

        {/* Form */}
        <div className="w-full">
          {/* Organisation Picture */}
          <div className="mb-6">
            <label className="block text-[#FEFCEB] text-sm font-medium mb-3">Organisation picture</label>
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-28 h-28 border border-dashed border-[#3a4453] rounded-full flex items-center justify-center overflow-hidden bg-transparent">
                  {renderAvatarImage()}
                </div>
                <button
                  type="button"
                  onClick={openModal}
                  className="absolute -top-2 -right-2 w-9 h-9 rounded-full bg-[#06203A] border border-[#3a4453] flex items-center justify-center hover:scale-110 transition-transform"
                >
                  <img src={uploadIcon} alt="Upload" className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Organisation Name */}
          <div className="mb-6">
            <label className="block text-[#FEFCEB] text-sm font-medium mb-2">Organisation name</label>
            <Input
              type="text"
              placeholder="Enter name here"
              value={organisationName}
              onChange={(e) => setOrganisationName(e.target.value)}
            />
          </div>

          {/* Error Message */}
          {submitError && (
            <p className="text-sm text-red-500 mb-4">{submitError}</p>
          )}

          {/* Finish Button */}
          <Button
            variant="primary"
            size="lg"
            className="w-full mb-3"
            onClick={handleFinish}
            disabled={isSubmitting}
          >
            Finish
          </Button>

          {/* Back Link */}
          <button
            type="button"
            onClick={handleBack}
            className="w-full text-center text-sm text-shaded-text hover:text-light-blue transition-colors"
          >
            Back to the location details
          </button>
        </div>
      </div>

      {/* Avatar Modal */}
      <ClubbieModal
        isOpen={isModalOpen}
        toggle={closeModal}
        title=""
        minHeight="auto"
        maxHeight="85vh"
        footer={
          <div className="flex justify-end gap-3 px-5 py-4 border-t border-blue-shade-9">
            <button
              type="button"
              onClick={handleCancelAvatar}
              className="px-6 py-2 rounded-full bg-transparent border border-blue-shade-7 text-[#FEFCEB] text-sm font-proxima hover:bg-blue-shade-9 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAvatar}
              disabled={!selectedAvatar && !selectedFile}
              className="px-6 py-2 rounded-full bg-[#00E7FF] text-[#081725] text-sm font-proxima font-semibold disabled:bg-blue-shade-7 disabled:text-shaded-text disabled:cursor-not-allowed hover:bg-[#00d4eb] transition-colors"
            >
              Confirm
            </button>
          </div>
        }
      >
        <div className="text-[#FEFCEB] px-5 py-5 max-w-md mx-auto text-center">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold">Add organization picture</h2>
            <button
              type="button"
              onClick={closeModal}
              className="w-6 h-6 flex items-center justify-center text-shaded-text hover:text-[#FEFCEB] text-xl"
            >
              ×
            </button>
          </div>

          {/* Preview Circle */}
          <div className="flex justify-center mb-5">
            <div className="w-28 h-28 rounded-full border border-blue-shade-7 flex items-center justify-center overflow-hidden bg-blue-shade-9">
              {selectedAvatar ? (
                <img
                  src={
                    UserIcon
                  }
                  alt="Selected"
                  className="w-full h-full object-cover"
                />
              ) : preview ? (
                <img src={preview} alt="Uploaded preview" className="w-full h-full object-cover" />
              ) : (
                <img src={UserIcon} alt="Organisation" className="w-10 h-10 opacity-30" />
              )}
            </div>
          </div>

          {/* Upload Button */}
          <div className="mb-4">
            <label
              htmlFor="orgFileInput"
              className="flex items-center justify-center gap-2 w-full h-10 rounded-full bg-[#082441] text-[#FEFCEB] text-sm font-proxima cursor-pointer hover:bg-[#0a2f57] transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10m9.333-5.333L8 1.333m0 0L4.667 4.667M8 1.333v9.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Upload new photo
            </label>
            <input
              id="orgFileInput"
              type="file"
              accept=".svg,.png,.jpg,.jpeg,.gif"
              onChange={handleFileUpload}
              className="hidden"
            />
            {validationError && (
              <p className="mt-2 text-xs text-red-500 text-center">{validationError}</p>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center my-4 text-xs text-shaded-text">
            <span className="flex-1 h-px bg-blue-shade-8" />
            <span className="px-3">or</span>
            <span className="flex-1 h-px bg-blue-shade-8" />
          </div>

          {/* Gallery Title */}
          <p className="text-xs text-shaded-text mb-3">Choose from the gallery</p>

          {/* Avatar Grid */}
          {/* <div className="grid grid-cols-3 gap-3">
            {avatarOptions.map((avatar) => (
              <button
                key={avatar.name}
                type="button"
                onClick={() => handleAvatarSelect(avatar.name)}
                className="relative w-full aspect-square rounded-full overflow-hidden border-2 transition-all hover:scale-105 bg-blue-shade-9"
                style={{
                  borderColor: selectedAvatar === avatar.name ? '#00E7FF' : 'transparent',
                }}
              >
                <img src={avatar.src} alt={avatar.name} className="w-full h-full object-cover" />
              </button>
            ))}
          </div> */}
        </div>
      </ClubbieModal>
    </div>
  );
}

export default OrganisationDetails;
